import { formatInTimeZone } from "date-fns-tz"
import { dayHasHours, type OperatingHours } from "@/lib/hours/openStatus"

/**
 * Estado de apertura AHORA, con cuánto lleva abierto el local.
 *
 * Por qué no basta `countOpenDays`: contar días de apertura silencia al local que
 * cierra tres días seguidos, pero no al que cierra uno. Su último cobro del
 * domingo a las 22:00 y el primero del martes a las 11:00 dejan 37 h de silencio
 * perfectamente normal, y el umbral de `terminal_inactive` son 24 h de RELOJ.
 * Medido el 2026-09-08: 12 de los 20 locales operativos cierran al menos un día
 * por semana, y 10 de las 14 críticas abiertas eran exactamente eso.
 *
 * `unknown` NO es `closed`: sin horario utilizable las reglas tienen que
 * comportarse como antes de que esto existiera, igual que el `null` de
 * countOpenDays. Un local sin `operating_hours` no puede perder monitoreo.
 */
export type OpenNowState =
  | { state: "unknown" }
  | { state: "closed" }
  /** Horas transcurridas desde que arrancó el intervalo de apertura vigente. */
  | { state: "open"; hoursSinceOpen: number }

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

/** "HH:mm" -> minutos desde medianoche. */
function parseHM(value: string | undefined | null): number {
  if (!value) return 0
  const [h, m] = value.split(":").map((n) => parseInt(n, 10))
  return (h || 0) * 60 + (m || 0)
}

function prevDay(day: string): string {
  const idx = DAY_ORDER.indexOf(day)
  return DAY_ORDER[(idx - 1 + 7) % 7]
}

/** La columna es jsonb: puede venir null, un escalar o un arreglo. */
function asOperatingHours(value: unknown): OperatingHours | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as OperatingHours
}

export function openStateNow(args: {
  operatingHours: unknown
  timezone: string | null | undefined
  nowMs: number
}): OpenNowState {
  const hours = asOperatingHours(args.operatingHours)
  if (!hours) return { state: "unknown" }
  // Un horario donde ningún día tiene horas completas es tan poco informativo
  // como no tenerlo (mismo criterio que countOpenDays).
  if (!Object.values(hours).some((d) => dayHasHours(d))) return { state: "unknown" }

  const tz = args.timezone?.trim() || "UTC"
  let dayName: string
  let nowMin: number
  try {
    const now = new Date(args.nowMs)
    dayName = formatInTimeZone(now, tz, "EEEE").toLowerCase()
    nowMin = parseHM(formatInTimeZone(now, tz, "HH:mm"))
  } catch {
    return { state: "unknown" } // huso inválido: mejor "no sé" que un cierre falso
  }

  const today = hours[dayName]
  if (dayHasHours(today)) {
    const o = parseHM(today.open)
    const c = parseHM(today.close)
    // Intervalo normal dentro del día.
    if (c > o && nowMin >= o && nowMin < c) return { state: "open", hoursSinceOpen: (nowMin - o) / 60 }
    // Abre hoy y cruza medianoche; todavía estamos antes de las 00:00.
    if (c < o && nowMin >= o) return { state: "open", hoursSinceOpen: (nowMin - o) / 60 }
  }

  // Madrugada que pertenece al día anterior (ayer cruzaba medianoche). Sin esto,
  // un local abierto de 18:00 a 01:00 figura como CERRADO a las 00:30 — su hora
  // más cargada— y la alerta se apagaría justo cuando importa.
  const prev = hours[prevDay(dayName)]
  if (dayHasHours(prev)) {
    const o = parseHM(prev.open)
    const c = parseHM(prev.close)
    if (c < o && nowMin < c) return { state: "open", hoursSinceOpen: (1440 - o + nowMin) / 60 }
  }

  return { state: "closed" }
}
